import React, { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, Tooltip, XAxis, YAxis } from 'recharts';

import { type ChartConfig, ChartContainer } from '@/components/ui/chart';
import { AXIS_LINE_STYLE, TICK_LINE_STYLE } from '@/pages/analytics/utils/chartAxis';
import { colorForSeriesIndex } from '@/pages/analytics/utils/chartPalette';
import { pivotSeries } from '@/pages/analytics/utils/series';

import { ChartEmptyState } from './ChartStates';
import { DotsLegend } from './DotsLegend';

interface StackedBarRow {
  x: string;
  group: string;
  group_name?: string;
  value: number;
}

interface StackedBarChartProps {
  rows: StackedBarRow[];
  /** Axis label for an x value. Defaults to the raw value. */
  xFormatter?: (x: string) => string;
  /** Tooltip heading for an x value. Defaults to `xFormatter`. */
  tooltipXFormatter?: (x: string) => string;
  /** Tooltip text for one series' value, e.g. "12 total calls". Defaults to the localised number. */
  valueFormatter?: (value: number) => string;
  /** Y axis tick label. Defaults to the localised number; charts of hours pass their own. */
  yFormatter?: (value: number) => string;
  /** x values to draw even when no row has them (empty buckets), in axis order. */
  xValues?: string[];
  /** Colour per group key; groups missing from it take the palette by position. */
  colorByKey?: Map<string, string>;
  /** What the chart is, for the accessible name ("Call volume bar chart"). */
  ariaLabelPrefix?: string;
  /** What a series is, for the accessible name ("program" gives "3 programs"). */
  seriesNoun?: string;
  height?: number;
}

// Thousands separators keep high-volume orgs readable (e.g. "34,058" not "34058").
const formatCount = (v: number) => v.toLocaleString('en-US');

/**
 * Stacked bars over long-form `{ x, group, value }` rows, one series per group. Shared by the
 * QC call-volume by-program view and the AE / PQC analytics trend and histogram charts.
 */
export const StackedBarChart: React.FC<StackedBarChartProps> = ({
  rows,
  xFormatter = x => x,
  tooltipXFormatter,
  valueFormatter = formatCount,
  yFormatter = formatCount,
  xValues,
  colorByKey,
  ariaLabelPrefix = 'Stacked bar chart',
  seriesNoun = 'series',
  height = 220,
}) => {
  const { data, series } = useMemo(() => {
    const pivot = pivotSeries(
      rows,
      r => r.x,
      r => r.value,
      'omit',
    );
    if (!xValues) return pivot;
    const byX = new Map(pivot.data.map(d => [d.date, d]));
    return { ...pivot, data: xValues.map(x => byX.get(x) ?? { date: x }) };
  }, [rows, xValues]);

  const colorFor = (key: string, index: number): string =>
    colorByKey?.get(key) ?? colorForSeriesIndex(index);

  const config: ChartConfig = useMemo(() => {
    const c: ChartConfig = {};
    series.forEach((s, index) => {
      c[s.key] = { label: s.name, color: colorByKey?.get(s.key) ?? colorForSeriesIndex(index) };
    });
    return c;
  }, [series, colorByKey]);

  if (!rows.length) {
    return <ChartEmptyState height={height} />;
  }

  return (
    <ChartContainer
      config={config}
      className="!aspect-auto w-full"
      style={{ height }}
      role="img"
      aria-label={`${ariaLabelPrefix}, ${series.length} ${seriesNoun}${series.length === 1 ? '' : 's'} across ${data.length} points`}
    >
      <BarChart data={data} margin={{ top: 10, right: 16, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="date"
          tickFormatter={value => xFormatter(String(value))}
          tickLine={TICK_LINE_STYLE}
          axisLine={AXIS_LINE_STYLE}
          fontSize={11}
        />
        <YAxis
          allowDecimals={false}
          tickFormatter={yFormatter}
          tickLine={TICK_LINE_STYLE}
          axisLine={AXIS_LINE_STYLE}
          fontSize={11}
          width={36}
        />
        <Tooltip
          contentStyle={{
            background: 'hsl(var(--background))',
            border: '1px solid hsl(var(--border))',
            borderRadius: 6,
            fontSize: 12,
          }}
          // Lift the tooltip above adjacent chart cards; without a z-index the
          // wrapper paints under the sibling chart in the 2-col grid (AQ-476).
          wrapperStyle={{ zIndex: 20 }}
          cursor={{ fill: 'hsl(var(--muted) / 0.4)' }}
          formatter={(value, name) => [
            typeof value === 'number' ? valueFormatter(value) : '',
            name,
          ]}
          labelFormatter={value => (tooltipXFormatter ?? xFormatter)(String(value))}
        />

        {series.map((s, index) => (
          <Bar
            key={s.key}
            dataKey={s.key}
            name={s.name}
            stackId="stack"
            fill={colorFor(s.key, index)}
            // Round only the top segment of each stack: the last series drawn is
            // the top of the stack in recharts.
            radius={index === series.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
            animationDuration={500}
          />
        ))}

        {series.length > 0 && (
          <Legend
            content={<DotsLegend />}
            verticalAlign="bottom"
            wrapperStyle={{ paddingTop: 4 }}
          />
        )}
      </BarChart>
    </ChartContainer>
  );
};
